<template>
  <v-container fluid>
    <v-card class="mx-auto mt-5" outlined tile>
      <v-toolbar flat>
        <v-toolbar-title>
          Registrations
        </v-toolbar-title>
        <v-spacer />
        <admin-registration-dialog
          v-bubble.error
          v-bubble.info
        />
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="6" align-self="start">
            <v-text-field
              label="Search"
              type="text"
              clearable
              append-icon="search"
              v-model="searchQuery"
              hide-details
              single-line
            ></v-text-field>
          </v-col>
          <v-col cols="2" align-self="start">
            <v-select
              label="Search by"
              v-model="searchParameter"
              :items="searchParameters"
              hide-details
              single-line
            ></v-select>
          </v-col>
          <v-col cols="2" align-self="start">
            <v-select
              clearable
              label="Member Type"
              item-text="name"
              v-model="memberType"
              :items="permittedMemberTypes"
              hide-details
              single-line
            ></v-select>
          </v-col>
          <v-col cols="2" align-self="start">
            <v-select
              clearable
              label="Sub-type"
              item-text="name"
              v-model="memberTypeSubType"
              :items="memberTypeSubTypes"
              hide-details
              single-line
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-tabs
              v-model="tab"
              class="mb-2"
            >
              <v-tabs-slider color="primary"></v-tabs-slider>
              <v-tab key="all">ALL</v-tab>
              <v-tab key="intervention-required">INTERVENTION REQUIRED</v-tab>
              <v-tab v-for="registrationStatus in RegistrationStatus" :key="registrationStatus">{{ registrationStatus }}</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">

              <!-- All registrations -->
              <v-tab-item key="all">
                <admin-registration-list
                  :filters="filters"
                  @open="onOpen"
                  v-bubble.error
                  v-bubble.info
                  persistenceKey="all"
                />
              </v-tab-item>

              <!-- Intervention required -->
              <v-tab-item key="intervention-required">
                <admin-registration-list
                  :filters="{ ...filters, ...interventionFilter }"
                  @open="onOpen"
                  v-bubble.error
                  v-bubble.info
                  persistenceKey="intervention-required"
                />
              </v-tab-item>

              <!-- By registration status -->
              <v-tab-item v-for="registrationStatus in RegistrationStatus" :key="registrationStatus">
                <admin-registration-list
                  :filters="{ ...filters, status: registrationStatus }"
                  @open="onOpen"
                  v-bubble.error
                  v-bubble.info
                  :persistenceKey="registrationStatus"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import _ from 'lodash'
import vuejsStorage from 'vuejs-storage'
import { RegistrationStatus, AccessType, LicenseStatus } from '@mymsa/common'
import AdminRegistrationList from '../components/AdminRegistrationList.vue'
import AdminRegistrationDialog from '../components/AdminRegistrationDialog.vue'

const adminAccessTypes = [AccessType.STAFF, AccessType.ADMINISTRATOR]

export default {
  name: 'AdminView',

  components: {
    AdminRegistrationList,
    AdminRegistrationDialog
  },

  data () {
    return {
      RegistrationStatus,
      searchParameter: 'memberNumberLike',
      searchParameters: [
        { text: 'Member Number', value: 'memberNumberLike' },
        { text: 'Member Name', value: 'memberNameLike' },
        { text: 'Entity Name', value: 'entityNameLike' },
        { text: 'Business Name', value: 'businessNameLike' }
      ],
      memberType: null,
      allMemberTypes: [],
      memberTypeSubType: null,
      searchQuery: '',
      tab: null
    }
  },

  storage: {
    keys: [
      'searchParameter',
      'memberType',
      'memberTypeSubType',
      'searchQuery',
      'tab'
    ],
    namespace: 'AdminView',
    driver: vuejsStorage.drivers.sessionStorage
  },

  computed: {
    accessType () {
      return this.$auth.userProfile.access_type || AccessType.RESTRICTED
    },

    filters () {
      const filters = {}

      if (this.searchQuery) {
        _.set(filters, this.searchParameter, this.searchQuery)
      }

      if (this.memberType) {
        _.set(filters, 'activities.type', this.memberType)
      }

      if (this.memberTypeSubType) {
        _.set(filters, 'activities.subType', this.memberTypeSubType)
      }

      // Limit to specific member types and license statuses based on user scope
      if (!adminAccessTypes.includes(this.accessType)) {
        const memberTypeFilters = []
        filters.OR = memberTypeFilters
        for (const permittedMemberType of this.permittedMemberTypes) {
          for (const permittedLicenseStatus of this.permittedLicenseStatuses) {
            memberTypeFilters.push({
              activities: {
                type: permittedMemberType.value,
                licenseStatus: permittedLicenseStatus
              }
            })
          }
        }
      }

      return filters
    },

    permittedMemberTypes () {
      let permittedMemberTypes = []
      if (this.accessType === AccessType.AUS_MEAT) {
        const memberTypeValues = ['PROCESSOR', 'INDEPENDENT_BONING_ROOM']
        permittedMemberTypes = this.allMemberTypes.filter(o => memberTypeValues.includes(o.value))
      } else if (adminAccessTypes.includes(this.accessType)) {
        permittedMemberTypes = this.allMemberTypes
      }
      return permittedMemberTypes
    },

    memberTypeSubTypes () {
      if (!this.memberType) return []

      const memberType = this.allMemberTypes.find(o => o.value === this.memberType)
      return memberType?.subTypes ?? []
    },

    permittedLicenseStatuses () {
      let permittedLicenseStatuses = []
      if (this.accessType === AccessType.AUS_MEAT) {
        permittedLicenseStatuses = [LicenseStatus.CURRENT, LicenseStatus.PENDING]
      } else if (adminAccessTypes.includes(this.accessType)) {
        permittedLicenseStatuses = Object.values(LicenseStatus)
      }
      return permittedLicenseStatuses
    },

    memberTypesWithSiteVisitRequired () {
      return this.permittedMemberTypes.filter(o => o.siteVisitRequired)
    },

    memberTypesWithOnSiteTrainingRequired () {
      return this.permittedMemberTypes.filter(o => o.trainingRequired)
    },

    interventionFilter () {
      const filters = [
        // With activities that have been submitted
        {
          activities: {
            registrationStatus: RegistrationStatus.SUBMITTED
          }
        },

        // Activities requiring site visit
        ...this.memberTypesWithSiteVisitRequired.map(memberType => ({
          activities: {
            type: memberType.value,
            siteVisitCompletedAt: null
          }
        })),

        // Activities requiring on-site training
        ...this.memberTypesWithOnSiteTrainingRequired.map(memberType => ({
          activities: {
            type: memberType.value,
            trainingCompletedAt: null
          }
        }))
      ]

      return {
        OR: filters
      }
    }
  },
  apollo: {
    allMemberTypes: {
      query: gql`query {
        allMemberTypes: memberTypeMany {
          _id
          name
          value
          trainingRequired
          siteVisitRequired
          subTypes {
            _id
            name
            value
          }
        }
      }`
    }
  },
  methods: {
    onOpen (item) {
      this.$router.push({ name: 'AdminRegistration', params: { id: item._id } })
    }
  }
}
</script>
